import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_fb7eabf4 from 'nuxt_plugin_sentryserver_fb7eabf4' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_1c87387e from 'nuxt_plugin_sentryclient_1c87387e' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_pluginutils_afe4238e from 'nuxt_plugin_pluginutils_afe4238e' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_db685d78 from 'nuxt_plugin_pluginrouting_db685d78' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_779c40ab from 'nuxt_plugin_pluginmain_779c40ab' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_moment_4b8264d4 from 'nuxt_plugin_moment_4b8264d4' // Source: ./moment.js (mode: 'all')
import nuxt_plugin_workbox_28a4b8de from 'nuxt_plugin_workbox_28a4b8de' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_metaplugin_6dc37c1e from 'nuxt_plugin_metaplugin_6dc37c1e' // Source: ./pwa/meta.plugin.js (mode: 'all')
import nuxt_plugin_iconplugin_64743136 from 'nuxt_plugin_iconplugin_64743136' // Source: ./pwa/icon.plugin.js (mode: 'all')
import nuxt_plugin_axios_c993677c from 'nuxt_plugin_axios_c993677c' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_i18n_926bd3dc from 'nuxt_plugin_i18n_926bd3dc' // Source: ../plugins/i18n (mode: 'all')
import nuxt_plugin_axios_2228ef02 from 'nuxt_plugin_axios_2228ef02' // Source: ../plugins/axios (mode: 'all')
import nuxt_plugin_routes_d26c9d0a from 'nuxt_plugin_routes_d26c9d0a' // Source: ../plugins/routes.client (mode: 'client')
import nuxt_plugin_framework7_6843c1ec from 'nuxt_plugin_framework7_6843c1ec' // Source: ../plugins/framework7.client (mode: 'client')
import nuxt_plugin_veevalidate_242894f8 from 'nuxt_plugin_veevalidate_242894f8' // Source: ../plugins/vee-validate.client (mode: 'client')
import nuxt_plugin_filters_3848d012 from 'nuxt_plugin_filters_3848d012' // Source: ../plugins/filters.client (mode: 'client')
import nuxt_plugin_mixins_35fed3b3 from 'nuxt_plugin_mixins_35fed3b3' // Source: ../plugins/mixins.client (mode: 'client')
import nuxt_plugin_bridge_0097305c from 'nuxt_plugin_bridge_0097305c' // Source: ../plugins/bridge.client (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root ? this.$root.$options.$nuxt : null
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const store = createStore(ssrContext)
  const router = await createRouter(ssrContext, config, { store })

  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"delivery-app","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":""}],"link":[],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      errPageReady: false,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        nuxt.errPageReady = false
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    beforeSerializeFns: ssrContext ? ssrContext.beforeSerializeFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_fb7eabf4 === 'function') {
    await nuxt_plugin_sentryserver_fb7eabf4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_1c87387e === 'function') {
    await nuxt_plugin_sentryclient_1c87387e(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_afe4238e === 'function') {
    await nuxt_plugin_pluginutils_afe4238e(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_db685d78 === 'function') {
    await nuxt_plugin_pluginrouting_db685d78(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_779c40ab === 'function') {
    await nuxt_plugin_pluginmain_779c40ab(app.context, inject)
  }

  if (typeof nuxt_plugin_moment_4b8264d4 === 'function') {
    await nuxt_plugin_moment_4b8264d4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_workbox_28a4b8de === 'function') {
    await nuxt_plugin_workbox_28a4b8de(app.context, inject)
  }

  if (typeof nuxt_plugin_metaplugin_6dc37c1e === 'function') {
    await nuxt_plugin_metaplugin_6dc37c1e(app.context, inject)
  }

  if (typeof nuxt_plugin_iconplugin_64743136 === 'function') {
    await nuxt_plugin_iconplugin_64743136(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_c993677c === 'function') {
    await nuxt_plugin_axios_c993677c(app.context, inject)
  }

  if (typeof nuxt_plugin_i18n_926bd3dc === 'function') {
    await nuxt_plugin_i18n_926bd3dc(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_2228ef02 === 'function') {
    await nuxt_plugin_axios_2228ef02(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_routes_d26c9d0a === 'function') {
    await nuxt_plugin_routes_d26c9d0a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_framework7_6843c1ec === 'function') {
    await nuxt_plugin_framework7_6843c1ec(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_veevalidate_242894f8 === 'function') {
    await nuxt_plugin_veevalidate_242894f8(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_filters_3848d012 === 'function') {
    await nuxt_plugin_filters_3848d012(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_mixins_35fed3b3 === 'function') {
    await nuxt_plugin_mixins_35fed3b3(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_bridge_0097305c === 'function') {
    await nuxt_plugin_bridge_0097305c(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
